/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Theme, useMediaQuery } from '@mui/material';

import {
  AccountSettings,
  AcceptPaymentsSettings,
  LeftMenu,
  ProfileSettings,
  StaffSettings,
  AINotesSettings,
  DocumentsSettings,
  FaxSettings,
  NotesGenerator,
  SubscriptionSettings,
  YourOrganizationSettings,
  TeleconsentSettings,
} from './components';
import { useSubscription } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { getIsAdminByRoles } from 'utils';

import styles from './SettingsPage.module.scss';
import { CancellationConfirmationPopupProvider } from './components/SubscriptionSettings/components/CancellationConfirmation/CancellationConfirmationPopupProvider';
import { isHipaaLink } from 'config/productProfile';
import { useSnackbar } from 'notistack';
import { FaxStatusMessageDto } from 'types/fax';
import { useSignalrConnection } from 'hooks';
import CallHistory from './components/CallHistory/CallHistory';
import { useTranslation } from 'react-i18next';

import faxDeliveredSound from 'assets/audio/fax/fax-delivered.mp3';
import faxFailedSound from 'assets/audio/fax/fax-failed.mp3';
import faxQueuedSound from 'assets/audio/fax/fax-queued.mp3';

export type MenuState =
  | 'profile'
  | 'accountSettings'
  | 'yourOrganization'
  | 'acceptPayments'
  | 'staff'
  | 'aiNotes'
  | 'documents'
  | 'faxes'
  | 'callHistory'
  | 'notesGenerator'
  | 'subscriptions'
  | 'teleconsent';

const AINOTES_LEGACY_HASH = 'ainotes';

function parseHashToMenuState(hash: string): MenuState | null {
  const raw = hash ? hash.substring(1) : '';
  if (!raw) return null;
  if (raw === AINOTES_LEGACY_HASH) return 'aiNotes';
  return raw as MenuState;
}

function buildHashFromMenuState(state: MenuState | null): string {
  if (!state) return '';
  if (state === 'aiNotes') return `#${AINOTES_LEGACY_HASH}`;
  return `#${state}`;
}

export function SettingsPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const subscription = useSubscription();
  const user = useAppSelector(selectUser);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('settings');
  const { events } = useSignalrConnection();

  const { hash, pathname, search } = location;
  const fromBurgerMenu = Boolean((location.state as { fromBurgerMenu?: boolean } | null)?.fromBurgerMenu);
  const [menuState, _setMenuState] = useState<MenuState | null>(parseHashToMenuState(hash));
  const [isLeftMenuLoading, setIsLeftMenuLoading] = useState<boolean>(!isMobile);
  const [isLeftMenuReady, setIsLeftMenuReady] = useState<boolean>(isMobile);
  const [isProfileDirty, setIsProfileDirty] = useState(false);

  const isAdmin = useMemo<boolean>(() => {
    return getIsAdminByRoles(user?.clinicUser.roles || []);
  }, [user]);

  const isMember = user?.clinicUser?.via3UserType === 'Member';
  const canAccessStaffTab = isAdmin;
  const canAccessFaxesTab = !isMember && !!user?.clinicUser?.canAccessFaxes;
  const canAccessCallHistoryTab = user?.clinicUser?.enableCallHistory !== false;

  const sanitizeMenuState = useCallback(
    (state: MenuState | null): MenuState | null => {
      if (!state) return state;

      if (state === 'staff' && !canAccessStaffTab) {
        return 'profile';
      }

      if (state === 'faxes' && !canAccessFaxesTab) {
        return 'profile';
      }

      if (state === 'callHistory' && !canAccessCallHistoryTab) {
        return 'profile';
      }

      return state;
    },
    [canAccessStaffTab, canAccessFaxesTab, canAccessCallHistoryTab],
  );

  const faxDeliveredAudioRef = useRef<HTMLAudioElement | null>(null);
  const faxFailedAudioRef = useRef<HTMLAudioElement | null>(null);
  const faxQueuedAudioRef = useRef<HTMLAudioElement | null>(null);

  const canReceiveFaxNotifications = useMemo(() => {
    if (!user?.clinicUser) return false;
    return user.clinicUser.canAccessFaxes === true;
  }, [user]);

  const playFaxSound = (status: string) => {
    const s = status.toLowerCase();

    try {
      if (s === 'failed' || s.includes('fail')) {
        faxFailedAudioRef.current?.play();
      } else if (s === 'sent' || s === 'delivered' || s.includes('deliver')) {
        faxDeliveredAudioRef.current?.play();
      }
    } catch {
      // never block UI
    }
  };

  useEffect(() => {
    if (!events || !canReceiveFaxNotifications) return;

    const handler = (message: FaxStatusMessageDto) => {
      const statusLower = message.status.toLowerCase();

      enqueueSnackbar(
        statusLower === 'failed'
          ? t('faxNotifications.failed')
          : statusLower === 'sent' || statusLower === 'delivered'
          ? t('faxNotifications.delivered')
          : t('faxNotifications.status', { status: message.status }),
        {
          variant:
            statusLower === 'failed'
              ? 'error'
              : statusLower === 'sent' || statusLower === 'delivered'
              ? 'success'
              : 'info',
        },
      );

      playFaxSound(message.status);
    };

    events.on('faxStatusUpdated', handler);

    return () => {
      events.off('faxStatusUpdated', handler);
    };
  }, [canReceiveFaxNotifications, enqueueSnackbar, events, t]);

  useEffect(() => {
    faxDeliveredAudioRef.current = new Audio(faxDeliveredSound);
    faxFailedAudioRef.current = new Audio(faxFailedSound);
    faxQueuedAudioRef.current = new Audio(faxQueuedSound);
  }, []);

  // commented temporary
  // const isExpired = useMemo<boolean>(() => {
  //   return (
  //     subscription?.subscriptionStatus === SubscriptionStatusEnum.Expired ||
  //     subscription?.subscriptionStatus === SubscriptionStatusEnum.TrialExpired
  //   );
  // }, [user]);

  const setMenuState = useCallback(
    (state: MenuState | null): void => {
      const nextHash = buildHashFromMenuState(state);
      if (hash !== nextHash) {
        navigate(
          {
            pathname,
            search,
            hash: nextHash,
          },
          { replace: true },
        );
      }
      _setMenuState(state);
    },
    [hash, navigate, pathname, search],
  );

  // commented temporary
  // useEffect(() => {
  //   if (!isMobile) {
  //     if (isAdmin && subscription?.hasSubscription && isExpired) {
  //       const confirmationProps = {
  //         title: 'Subscription Expired',
  //         message: 'Upgrade now to continue using HIPAA LINK.',
  //       };
  //       confirm({
  //         content: ConfirmationDialog,
  //         props: confirmationProps,
  //       }).then(() => setMenuState('subscriptions'));
  //       return;
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const requested = parseHashToMenuState(hash);
    const sanitized = sanitizeMenuState(requested);

    if (requested !== sanitized) {
      const nextHash = buildHashFromMenuState(sanitized);
      navigate(
        {
          pathname,
          search,
          hash: nextHash,
        },
        { replace: true },
      );
    }

    _setMenuState(sanitized);
  }, [hash, navigate, pathname, sanitizeMenuState, search]);

  useEffect(() => {
    if (!isMobile || !fromBurgerMenu) {
      return;
    }

    _setMenuState(null);
    navigate(
      {
        pathname,
        search,
        hash: '',
      },
      { replace: true, state: null },
    );
  }, [fromBurgerMenu, isMobile, navigate, pathname, search]);

  useEffect(() => {
    if (!isMobile && !menuState) {
      setMenuState('profile');
    }
  }, [menuState]);

  const handleMenuStateChange = (state: MenuState | null): void => {
    const safeState = sanitizeMenuState(state);
    if (safeState === menuState) {
      return;
    }

    if (menuState === 'profile' && isProfileDirty && safeState !== 'profile') {
      if (!window.confirm(t('unsavedChangesPrompt'))) {
        return;
      }

      setIsProfileDirty(false);
    }

    if (isAdmin && subscription?.hasSubscription && subscription?.isSubscriptionActive) {
      setMenuState(safeState);
      return;
    }

    // commented temporary
    // const isAllowedMenuItem = state === 'yourOrganization' || state === 'staff' || state === 'subscriptions';
    // if (isAdmin && !isAllowedMenuItem && subscription?.hasSubscription && !subscription?.isSubscriptionActive) {
    //   setMenuState(state);
    //   const confirmationProps = {
    //     title: 'Subscription Expired',
    //     message: 'Upgrade now to continue using HIPAA LINK.',
    //   };
    //   confirm({ content: ConfirmationDialog, props: confirmationProps }).then(() => setMenuState('subscriptions'));
    //   return;
    // }

    // if (isAdmin && isAllowedMenuItem && subscription?.hasSubscription && !subscription?.isSubscriptionActive) {
    //   setMenuState(state);
    //   return;
    // }

    if (isAdmin && !subscription?.hasSubscription) {
      // TODO: what need to do in this case?
      // For now
      setMenuState(safeState);
      return;
    }

    setMenuState(safeState);
  };

  const handleBackToSettingsClick = (): void => {
    if (menuState === 'profile' && isProfileDirty && !window.confirm(t('unsavedChangesPrompt'))) {
      return;
    }

    setIsProfileDirty(false);
    setMenuState(null);
  };

  useEffect(() => {
    if (isMobile) {
      setIsLeftMenuLoading(false);
      setIsLeftMenuReady(true);
      return;
    }
    if (!isLeftMenuLoading) {
      setIsLeftMenuReady(true);
    }
  }, [isMobile, isLeftMenuLoading]);

  const shouldRenderLeftMenu = !isMobile || !menuState;
  // Desktop: block initial main-pane mount until the left menu has completed first load.
  // After first readiness, keep main mounted during later left-menu refreshes.
  // Mobile: keep existing behavior to avoid showing content before menu transition completes.
  const shouldRenderMain = isMobile ? (!shouldRenderLeftMenu || !isLeftMenuLoading) && !!menuState : isLeftMenuReady;

  return (
    <div className={styles.settingsPage}>
      {shouldRenderLeftMenu && (
        <LeftMenu
          menuState={menuState}
          onMenuStateChange={handleMenuStateChange}
          onLoadingChange={setIsLeftMenuLoading}
        />
      )}

      {shouldRenderMain && (
        <div className={styles.main}>
          {menuState === 'profile' && (
            <ProfileSettings onBackToSettingsClick={handleBackToSettingsClick} onDirtyChange={setIsProfileDirty} />
          )}
          {menuState === 'accountSettings' && <AccountSettings onBackToSettingsClick={handleBackToSettingsClick} />}
          {menuState === 'yourOrganization' && (
            <YourOrganizationSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {isHipaaLink && menuState === 'acceptPayments' && (
            <AcceptPaymentsSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {menuState === 'callHistory' && canAccessCallHistoryTab && (
            <CallHistory onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {menuState === 'staff' && canAccessStaffTab && (
            <StaffSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {isHipaaLink && menuState === 'aiNotes' && (
            <AINotesSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {menuState === 'documents' && <DocumentsSettings onBackToSettingsClick={handleBackToSettingsClick} />}
          {!isHipaaLink && menuState === 'faxes' && canAccessFaxesTab && (
            <FaxSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {isHipaaLink && menuState === 'notesGenerator' && (
            <NotesGenerator onBackToSettingsClick={handleBackToSettingsClick} />
          )}
          {isHipaaLink && menuState === 'subscriptions' && (
            <CancellationConfirmationPopupProvider>
              <SubscriptionSettings onBackToSettingsClick={handleBackToSettingsClick} />
            </CancellationConfirmationPopupProvider>
          )}
          {isHipaaLink && isAdmin && menuState === 'teleconsent' && (
            <TeleconsentSettings onBackToSettingsClick={handleBackToSettingsClick} />
          )}
        </div>
      )}
    </div>
  );
}
